import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-hot-toast";
import { Mail, Lock, Eye, EyeOff, ArrowRight } from "lucide-react";
import { AppDispatch } from "../app/store";
import { detailsInterface, loginThunk } from "../features/actions";
import { CircleLoader } from "../components/CircleLoader";
import logo from "../assets/white_logo.png";
import "../assets/css/main.css";

export const Login = () => {
  const [loading, setLoading] = useState(false);
  const [maskPassword, setMaskPassword] = useState(true);
  const [rememberMe, setRememberMe] = useState(true);
  const [signUp, setSignUp] = useState({ email: "", password: "" });

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSignUp({ ...signUp, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    window.sessionStorage.removeItem("token");

    const data: detailsInterface = {
      email: signUp.email,
      password: signUp.password,
    };

    try {
      const resultAction = await dispatch(loginThunk(data));
      const payload = resultAction.payload;

      if (typeof payload === "string") {
        throw new Error(payload);
      }

      sessionStorage.setItem("user", JSON.stringify(payload.data));
      sessionStorage.setItem("token", payload.token);

      const user = payload.data;
      const token = payload.token;

      if (user.role === "teacher") {
        navigate("/staff", { state: { token } });
      } else if (user.role === "super_admin" || user.role === "admin") {
        navigate("/admin", { state: { token } });
      } else {
        navigate("/staff", { state: { token } });
      }
    } catch (err: any) {
      toast.error(err.message || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (window.sessionStorage.getItem("token")) {
      navigate("/admin");
    }
  }, [navigate]);

  // Design tokens based on your premium settings style
  const labelStyle =
    "text-[11px] font-black text-slate-400 uppercase tracking-[0.15em] mb-2 block ml-1";
  const inputContainer =
    "relative flex items-center bg-slate-50 rounded-2xl border-2 border-transparent focus-within:border-blue-500 focus-within:bg-white transition-all px-5 py-4";
  const inputStyle =
    "w-full bg-transparent outline-none text-slate-700 font-bold text-sm placeholder:text-slate-300";

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 min-h-screen bg-white">
      {/* Left Side: Branding & Quote */}
      <div className="hidden lg:flex flex-col justify-between p-16 bg-[#012063] relative overflow-hidden">
        {/* Abstract Background Shapes */}
        <div className="absolute top-[-10%] right-[-10%] w-64 h-64 bg-blue-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-[-5%] left-[-5%] w-96 h-96 bg-blue-400/10 rounded-full blur-3xl" />

        <div className="relative z-10">
          <img src={logo} alt="Logo" className="h-12 w-auto mb-2" />
          <div className="w-12 h-1 bg-blue-400 mt-4 rounded-full" />
        </div>

        <div className="relative z-10 max-w-md">
          <h4 className="text-blue-400 font-black text-xs uppercase tracking-[0.3em] mb-4">
            Insight of the day
          </h4>
          <p className="text-white text-3xl font-bold leading-tight italic">
            “Education is the most powerful weapon which you can use to change
            the world.”
          </p>
          <div className="flex items-center gap-4 mt-8">
            <div className="w-10 h-[2px] bg-blue-500" />
            <p className="text-blue-200 font-black uppercase tracking-widest text-[10px]">
              Nelson Mandela
            </p>
          </div>
        </div>
      </div>

      {/* Right Side: Login Form */}
      <div className="flex items-center justify-center p-8 lg:p-24 bg-white">
        <div className="w-full max-w-[420px] animate-in fade-in slide-in-from-bottom-8 duration-700">
          <div className="mb-10 text-center lg:text-left">
            <h2 className="text-[32px] font-black text-slate-900 tracking-tighter mb-2">
              Welcome Back
            </h2>
            <p className="text-slate-400 font-bold text-sm">
              Enter your administrative credentials to continue.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email Field */}
            <div className="space-y-1">
              <label className={labelStyle}>Email Address</label>
              <div className={inputContainer}>
                <Mail className="text-slate-300 mr-4" size={18} />
                <input
                  name="email"
                  type="email"
                  value={signUp.email}
                  onChange={handleChange}
                  placeholder="admin@school.com"
                  className={inputStyle}
                  required
                />
              </div>
            </div>

            {/* Password Field */}
            <div className="space-y-1">
              <div className="flex justify-between items-end">
                <label className={labelStyle}>Password</label>
                <Link
                  to="/forgot-password"
                  className="text-[10px] font-black text-blue-600 uppercase tracking-widest mb-2 hover:text-blue-800 transition-colors"
                >
                  Forgot?
                </Link>
              </div>
              <div className={inputContainer}>
                <Lock className="text-slate-300 mr-4" size={18} />
                <input
                  name="password"
                  type={maskPassword ? "password" : "text"}
                  value={signUp.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  className={inputStyle}
                  required
                />
                <button
                  type="button"
                  onClick={() => setMaskPassword(!maskPassword)}
                  className="text-slate-300 hover:text-blue-500 transition-colors ml-2"
                >
                  {maskPassword ? <Eye size={18} /> : <EyeOff size={18} />}
                </button>
              </div>
            </div>

            {/* Keep Signed In */}
            <label className="flex items-center group cursor-pointer w-fit">
              <div className="relative flex items-center">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="peer appearance-none w-5 h-5 border-2 border-slate-200 rounded-lg checked:bg-blue-600 checked:border-blue-600 transition-all cursor-pointer"
                />
                <div className="absolute text-white opacity-0 peer-checked:opacity-100 pointer-events-none left-1">
                  <svg className="w-3 h-3 fill-current" viewBox="0 0 20 20">
                    <path d="M0 11l2-2 5 5L18 3l2 2L7 18z" />
                  </svg>
                </div>
              </div>
              <span className="ml-3 text-xs font-black text-slate-500 uppercase tracking-widest group-hover:text-slate-700 transition-colors">
                Keep me signed in
              </span>
            </label>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full h-16 bg-[#012063] text-white rounded-2xl font-black text-xs uppercase tracking-[0.2em] shadow-xl shadow-blue-900/20 hover:bg-[#082a7a] hover:-translate-y-1 transition-all flex items-center justify-center gap-3 active:scale-95"
            >
              {loading ? (
                <CircleLoader />
              ) : (
                <>
                  Login <ArrowRight size={16} />
                </>
              )}
            </button>
          </form>

          <p className="mt-12 text-center text-[10px] font-black text-slate-300 uppercase tracking-[0.2em]">
            © 2026 Your School System • Secure Admin Access
          </p>
        </div>
      </div>
    </div>
  );
};
